import { useState, useEffect, useCallback, useRef } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    UfoCustomTimingKey,
    ufoEventOnboardingWizardProjectCreation,
    ufoEventOnboardingWizardProjectCreationIncludingProvisioning
} from "@/components/signup/utils";
import {
    useBxpWebStargateClient,
    WizardCreateProjectPayload
} from "@/libraries/bxp-web-stargate-client/src";
import {
    useCloudStatus,
    useCloudStatusRedirection
} from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";
import { FeatureExposed } from "@/utils/feature-exposed";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { getProductQueryParamTeamType } from "../../../models/user-segmentation";
import { ANALYTICS_PREFIX } from "../common/constants";
import { useWizardConfiguration } from "../controllers";
import {
    useIsAllowExtraFieldsExperiment,
    useIsSimplifiedLandingViewWithBasicTasks,
    useIsWizardDevKanbanExperiment,
    useIsJFindInviteStepExperiment,
    useIsBasicSampleTasksForScrumOrSoftwareKanban
} from "../hooks/experiment-hooks";
import {
    onRedirectFailure,
    onRedirectSuccess
} from "../utils/redirect-logging";
import { ProjectConfig } from "../utils/types";

import { JiraOverlayShim } from "./jira-overlay-shim";
import { WelcomeModalPageSwitcher } from "./page-switcher/page-switcher";
import { useJfind1663TaskPolling } from "./variant/jfind-1663-page-invite/hooks/use-jfind-1663-create-project-task-polling-context";
import { useJfind1663FetchInterceptor } from "./variant/jfind-1663-page-invite/hooks/use-jfind-1663-fetch-interceptor";

export const WelcomeModal = ({
    onComplete,
    withSampleData
}: {
    onComplete: (taskId?: string, onRedirect?: () => void) => void;
    withSampleData: boolean;
}) => {
    // HOOKS
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { createCloudStatus, cloudId } = useCloudStatus();
    const isCreateRequestInProgress = useRef(false);
    const { signupId, taskIdParam } = useSearchParameters();
    const stargateClient = useBxpWebStargateClient();
    const { teamType, projectDetails } = useWizardConfiguration();
    const isWizardDevKanbanExperimentEnabled = useIsWizardDevKanbanExperiment();
    const { allowExtraFieldsExperimentConfigFireExposure } =
        useIsAllowExtraFieldsExperiment();
    const {
        simplifiedLandingViewWithBasicTasksIsEnrolledInTest,
        simplifiedLandingViewWithBasicTasksFireExposure
    } = useIsSimplifiedLandingViewWithBasicTasks();
    const {
        basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest,
        basicSampleTasksForScrumOrSoftwareKanbanFireExposure
    } = useIsBasicSampleTasksForScrumOrSoftwareKanban();
    const { deferRedirectView, setDeferRedirectView } =
        useCloudStatusRedirection();
    const isJFindInviteStepExperiment = useIsJFindInviteStepExperiment();
    const { setTaskId } = useJfind1663TaskPolling();
    const jfindProjectCreatedCheck = checkFeatureFlagGate(
        featureFlags.jfindProjectCreatedCheck
    );

    // STATE
    const [projectConfig, setProjectConfig] = useState<ProjectConfig | null>(
        null
    );

    useEffect(() => {
        createAnalyticsEvent({
            eventType: "screen",
            name: `${ANALYTICS_PREFIX}Screen`,
            attributes: {
                teamType: teamType,
                template: projectDetails.template
            }
        }).fire();
    }, [createAnalyticsEvent, projectDetails.template, teamType]);

    // Prevent use-cloud-status.tsx from immediately redirecting when provisioning is finished.
    useEffect(() => {
        if (!deferRedirectView) {
            if (setDeferRedirectView) {
                setDeferRedirectView("jira-onboarding-wizard");
            }
        }
    });

    // eslint-disable-next-line complexity
    const createProject = useCallback(async () => {
        if (projectConfig && cloudId) {
            ufoEventOnboardingWizardProjectCreation.start();

            ufoEventOnboardingWizardProjectCreation.setCustomData({ signupId });

            ufoEventOnboardingWizardProjectCreationIncludingProvisioning.startSubstep(
                UfoCustomTimingKey.ONBOARDING_WIZARD_PROJECT_CREATE_API_CALL
            );

            try {
                if (simplifiedLandingViewWithBasicTasksFireExposure) {
                    simplifiedLandingViewWithBasicTasksFireExposure();
                }

                if (basicSampleTasksForScrumOrSoftwareKanbanFireExposure) {
                    basicSampleTasksForScrumOrSoftwareKanbanFireExposure();
                }

                if (allowExtraFieldsExperimentConfigFireExposure) {
                    allowExtraFieldsExperimentConfigFireExposure();
                }

                isCreateRequestInProgress.current = true;

                const isSampleDataIncluded = !!(
                    withSampleData ||
                    isWizardDevKanbanExperimentEnabled ||
                    simplifiedLandingViewWithBasicTasksIsEnrolledInTest ||
                    basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest
                );

                const projectPayload: WizardCreateProjectPayload = {
                    projectName: projectConfig.projectName,
                    issueTypes: projectConfig.issueTypes,
                    statusNames: projectConfig.columns,
                    projectKey: projectDetails.projectKey,
                    template: projectDetails.template,
                    cloudId: cloudId,
                    teamType: getProductQueryParamTeamType(teamType),
                    isSampleDataIncluded,
                    customDetail: {
                        enableExtraFields: true,
                        ...(simplifiedLandingViewWithBasicTasksIsEnrolledInTest
                            ? { sampleDataVersion: "GENERIC" }
                            : {}),
                        ...(withSampleData || isWizardDevKanbanExperimentEnabled
                            ? { sampleDataVersion: "WIZARD" }
                            : {}),
                        ...(basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest
                            ? { sampleDataVersion: "GENERICV2" }
                            : {})
                    }
                };
                const { taskId } = await stargateClient.wizardProjectCreate(
                    projectPayload
                );

                // bug fix to not fire project create more than once
                if (taskId && jfindProjectCreatedCheck) {
                    const url = new URL(window.location.href);
                    url.searchParams.append("taskId", taskId);
                    history.replaceState(null, "", url.toString());
                }

                if (isJFindInviteStepExperiment) {
                    setTaskId(taskId);
                }

                ufoEventOnboardingWizardProjectCreation.success({
                    customData: {
                        template: projectDetails.template
                    }
                });

                if (!isJFindInviteStepExperiment) {
                    onComplete(
                        taskId,
                        onRedirectSuccess(projectDetails.template)
                    );
                }
            } catch (error) {
                ufoEventOnboardingWizardProjectCreation.failure({
                    customData: {
                        template: projectDetails.template
                    },
                    error
                });

                if (!isJFindInviteStepExperiment) {
                    onComplete(
                        undefined,
                        onRedirectFailure(projectDetails.template, error)
                    );
                }
                setTaskId(null); // null taskId indicates project creation failure
            } finally {
                if (
                    checkFeatureFlagGate(
                        featureFlags.jiraOnboardingMultipleProjectRequestsBugfix
                    )
                ) {
                    // skip updating isCreateRequestInProgress ref.
                    return;
                }

                isCreateRequestInProgress.current = false;
            }
        }
    }, [
        projectConfig,
        cloudId,
        signupId,
        simplifiedLandingViewWithBasicTasksFireExposure,
        basicSampleTasksForScrumOrSoftwareKanbanFireExposure,
        allowExtraFieldsExperimentConfigFireExposure,
        withSampleData,
        isWizardDevKanbanExperimentEnabled,
        simplifiedLandingViewWithBasicTasksIsEnrolledInTest,
        basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest,
        projectDetails.projectKey,
        projectDetails.template,
        teamType,
        stargateClient,
        jfindProjectCreatedCheck,
        isJFindInviteStepExperiment,
        setTaskId,
        onComplete
    ]);

    useEffect(() => {
        // Only trigger creating project once the site is ready
        let canCreateProject;

        if (jfindProjectCreatedCheck) {
            canCreateProject =
                projectConfig &&
                createCloudStatus === "success" &&
                cloudId &&
                !taskIdParam;
        } else {
            canCreateProject =
                projectConfig && createCloudStatus === "success" && cloudId;
        }

        if (canCreateProject && !isCreateRequestInProgress.current) {
            createProject();
        }
    }, [
        createCloudStatus,
        cloudId,
        createProject,
        projectConfig,
        jfindProjectCreatedCheck,
        taskIdParam
    ]);

    useEffect(() => {
        if (createCloudStatus === "success") {
            createAnalyticsEvent({
                eventType: "ui",
                action: "succeeded",
                actionSubject: "provisioning",
                actionSubjectId: "jiraAnimatedProvisioningScreen",
                source: "/ondemand/signup/confirmation",
                attributes: {
                    atlasId: "ATLAS-56326",
                    reactComponent: "WelcomeModal",
                    template: projectDetails.template,
                    teamType: teamType
                }
            }).fire();
        }
    }, [
        createAnalyticsEvent,
        createCloudStatus,
        projectDetails.template,
        teamType
    ]);

    useJfind1663FetchInterceptor({ cloudId });

    return (
        <FeatureExposed
            experimentKey="jfind-1565-sign-up-flow-overlay"
            group="Test"
        >
            <JiraOverlayShim>
                <WelcomeModalPageSwitcher
                    setProjectConfig={setProjectConfig}
                    onComplete={onComplete}
                />
            </JiraOverlayShim>
        </FeatureExposed>
    );
};
